import moment from 'moment-timezone';

const dateRangeLabels = {
  future: 'in %s',
  past: '%s ago',
  s: 'few seconds',
  ss: '%d seconds',
  m: '1 minute',
  mm: '%d minutes',
  h: '1 hour',
  hh: '%d hours',
  d: '1 day',
  dd: '%d days',
  M: '1 month',
  MM: '%d months',
  y: '1 year',
  yy: '%d years',
};

/**
 * Used in thread view for formatting timestamps, takes utc string input
 * @param  {String}  time
 * @param  {String}  [format='ddd, MMM D, h:mm a']
 * @return {String}
 */
export function formatTimestamp(time, format = 'ddd, MMM D, Y, h:mm a', overMinutePrefix = false) {
  let formattedTime = null;
  const currentTime = moment();
  const duration = moment.duration(currentTime.diff(convertUtcStringToLocalMoment(time)));

  // Read and "humanize" duration
  if (duration.asMinutes() < 1) {
    formattedTime = overMinutePrefix ? 'just now' : 'Just now';
  } else if (duration.asMinutes() < 60) {
    const minutes = Math.round(duration.asMinutes());

    formattedTime = `${minutes} ${minutes > 1 ? 'minutes' : 'minute'} ago`;
  } else if (overMinutePrefix) {
    formattedTime = `on ${moment.utc(time).local().format(format)}`;
  } else {
    formattedTime = moment.utc(time).local().format(format);
  }

  return formattedTime;
}

/**
 * Grab full timestamp (including year)
 * @param  {String} time
 * @param  {String} [format='ddd, MMM D, Y, h:mm a']
 * @return {String}
 */
export function fullTimestamp(time, format = 'dddd, MMMM D, Y, h:mm a') {
  return moment.utc(time).local().format(format);
}

// Takes a utc formatted string and returns in local string format
export function formatUtcToLocal(utcDate, format = 'MMM D, h:mm a') {
  return moment.utc(utcDate).local().format(format);
}

// Takes a utc time string and returns in local time string format
export function formatUtcTimeToLocal(utcTime) {
  const split = utcTime.split(':');
  const today = moment().utc();

  return moment(today).set({
    hour: split[0],
    minute: split[1],
    second: split[2],
  }).local().format('HH:mm:ss');
}

// Takes a local time string and returns in utc time string format
export function formatLocalTimeToUtc(localTime) {
  const split = localTime.split(':');
  const today = moment().local().format();

  return moment(today).set({
    hour: split[0],
    minute: split[1],
    second: split[2],
  }).utc().format('HH:mm:ss');
}

export function getStartAndEndOfDateUtc(fromDate, toDate) {
  const startDate = moment(fromDate).startOf('days').format();
  const endDate = moment(toDate).endOf('days').format();
  const startDateUtc = moment.utc(startDate).format('YYYY-MM-DD HH:mm:ss');
  const endDateUtc = moment.utc(endDate).format('YYYY-MM-DD HH:mm:ss');
  return {
    startDateUtc,
    endDateUtc,
  };
}

// Takes a utc string and returns a local moment
export function convertUtcStringToLocalMoment(utc) {
  return moment.utc(utc).local();
}

/**
 * Takes to and from as unix timestamp
 * @param  {number} [fromDate = milliseconds]
 * @param  {number} [toDate = milliseconds]
 * @return {string}
 */
export const formattedDateDifference = (fromDate, toDate = moment.utc()) => {
  moment.updateLocale('en', { relativeTime: dateRangeLabels });
  const fromUTC = moment.utc(fromDate);
  const toUTC = moment.utc(toDate);
  const difference = toUTC.from(fromUTC, true);
  moment.updateLocale('en', null);
  return difference;
};

// Takes a local time string and returns a local moment
export function convertLocalTimeToLocalMoment(localTime) {
  const split = localTime.split(':');
  const today = moment().format();

  return moment(today).set({
    hour: split[0],
    minute: split[1],
    second: split[2],
  });
}

export function isEndOfDay(utcDate) {
  const localMoment = convertUtcStringToLocalMoment(utcDate);
  return localMoment.hour() === 23 && localMoment.minute() === 59 && localMoment.second() === 59;
}

export function formatTimeZone(timeZone) {
  return `${timeZone.name} Time (UTC ${timeZone.utcOffset}) - ${timeZone.location}`;
}

export function convertDateInTimeZone(utcDate, timeZone, observesDst = true) {
  const convertedTimeZoneDate = moment.utc(utcDate).tz(timeZone);

  // if date in DST and channel doesn't observe DST, then reduce offset by one hour
  if (!observesDst && convertedTimeZoneDate.isDST()) {
    convertedTimeZoneDate.subtract(1, 'hour');
  }

  return convertedTimeZoneDate;
}

export function formatEpochTimestamp(epochTimestamp) {
  return moment(epochTimestamp, 'x').format('MM/DD/YY h:mm a');
}

export function formatYear(year) {
  if (year === -1) {
    return year;
  }
  return year ? moment([year]).format('YY') : year;
}

export function sortByDate(current, next, format) {
  const currentDate = moment(current, format);
  const nextDate = moment(next, format);
  if (currentDate.isBefore(nextDate)) {
    return -1;
  } else if (nextDate.isBefore(currentDate)) {
    return 1;
  } else {
    return 0;
  }
}

export function convertMomentToUnixTimestampMs(date) {
  return date.valueOf();
}

/**
 * @param {string} unixTimestampMs
 * @returns {string}
 * @description this will convert unixTimestampMs to UTC date in string
 */
export function convertUnixTimestampMsToUTCDateString(unixTimestampMs) {
  return moment(Number(unixTimestampMs)).utc().format();
}

/**
 * @param {string} unixTimestampMs
 * @param {string} endUnit
 * @returns {string}
 * @description this will convert unixTimestampMs to end time of UTC date in string
 */
export function convertUnixTimestampMsToUTCEndDateString(unixTimestampMs, endUnit = 'day') {
  return moment(Number(unixTimestampMs)).endOf(endUnit).utc().format();
}

export function findDurationFromMilliseconds(milliseconds) {
  const duration = moment.duration(milliseconds);
  const { hours, seconds, minutes } = duration._data;
  return `${hours ? `${hours} hours, ` : ''}${minutes ? `${minutes} minutes and ` : ''}${seconds ? `${seconds} seconds` : ''}`;
}

export function filterTimeZoneAbbreviation(timeZone) {
  let returnVal;
  switch (timeZone) {
    case 'EST':
    case 'EDT':
      returnVal = 'ET';
      break;
    case 'CST':
    case 'CDT':
      returnVal = 'CT';
      break;
    case 'MST':
    case 'MDT':
      returnVal = 'MT';
      break;
    case 'PST':
    case 'PDT':
      returnVal = 'PT';
      break;
    case 'AKDT':
    case 'AKST':
      returnVal = 'AKT';
      break;
    case 'HST':
    case 'HDT':
      returnVal = 'HT';
      break;
    default:
      break;
  }

  return returnVal;
}

// Return date in Format of Fri, Aug 1, 2020
export function updatedAtDateFormatter(date) {
  return moment(date).format('ddd, MMM DD, YYYY');
}

/**
 * Return numeric date from date string
 * @param  {String} date
 * @return {String}
 */
export function numericDateFormatter(date) {
  return moment(date).format('MM/DD/YY h:mm a');
}

export const convertHoursToDays = (numericHours, displayHours) => {
  if (numericHours > 0) {
    const duration = moment.duration(parseInt(numericHours, 10), 'hours');
    const hours = duration.hours();
    const isHoursPlural = hours > 1;
    const days = Math.floor(duration.asDays());
    const isDaysPlural = days > 1;
    let convertedString = '';
    if (days > 0) {
      convertedString = `${days} ${isDaysPlural ? 'Days ' : 'Day '}`;
    }
    if (hours > 0) {
      convertedString += `${days !== 0 || displayHours ? hours : ''} ${isHoursPlural ? 'Hours' : 'Hour'}`;
    }
    return convertedString.trim();
  }
  return '';
};
